import { Range } from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../../../common'
import type { Activity } from '../activity'
import type { Execution } from '../execution'
import { Item } from '../item'
import type { Process } from '../process'

type TemplateBase<TId extends Template.Id> = Readonly<
  common.Identified<TId> & common.Named & common.PotentiallyDescribed
>
/**
 * An outline of a process
 */
export class Template<TId extends Template.Id = Template.Id>
  implements TemplateBase<TId>
{
  public readonly id: TId
  public readonly name: string
  public readonly description?: string | undefined

  private readonly _steps: readonly Readonly<Template.StepConfig.Producer>[]

  constructor({
    id,
    name,
    description,
    steps,
  }: Readonly<Template.Params<TId>>) {
    this.id = id
    this.name = name
    this.description = description
    this._steps = steps
  }

  public getSteps(essentials: Process.Essentials): readonly Process.Step[] {
    const activityExecutionsByType: Partial<
      Record<Activity.Type, Activity.Execution[]>
    > = {}
    // Create a lookup by type for activity executions
    for (const activityExecutionId in essentials.activityExecutions) {
      const activityExecution =
        essentials.activityExecutions[activityExecutionId]
      if (!activityExecution) {
        continue
      }
      const activityExecutionsForCurrentType = (activityExecutionsByType[
        activityExecution.type
      ] ??= [])
      activityExecutionsForCurrentType.push(activityExecution)
    }

    // Deduplicate items to avoid duplicate steps when items are registered
    // multiple times (e.g., during parsing and during batch put). Uses both
    // type and id to distinguish items of different types that share the same
    // id (e.g., a Pool and an Allocation for the same FA category).
    const uniqueItems = essentials.items.filter(
      (item, index, self) =>
        self.findIndex(i => Item.Reference.isEqual(i, item)) === index,
    )

    const configs = this._steps.flatMap<Template.StepConfig>(
      producer =>
        producer.withoutItems ?? producer.basedOnItems?.(uniqueItems) ?? [],
    )

    return configs.flatMap<Process.Step>(config => {
      const executions = activityExecutionsByType[config.type] ?? []
      if (!executions.length) {
        // TODO: explain
        const isOptional =
          config.optional === true ||
          !!config.optional?.until.every(
            dependencyActivityType =>
              !!activityExecutionsByType[dependencyActivityType]?.length,
          )
        if (isOptional) {
          // If this config is optional, we don't have any steps to show
          return []
        }
        // TODO: explain - not started
        return {
          description: config.description,
          name: config.name,
          timeframe: Range.empty(),
        } satisfies Process.Step
      }

      const matchingExecutions = executions.filter(execution =>
        Item.Reference.isEqual(execution.item, config.item),
      )

      // When a config targets a specific item but no execution matches it
      // yet (e.g., entity hasn't started importing), show as "not started"
      // rather than hiding it entirely.
      if (!matchingExecutions.length) {
        return {
          description: config.description,
          name: config.name,
          timeframe: Range.empty(),
        } satisfies Process.Step
      }

      return matchingExecutions.map(
        execution =>
          ({
            description: config.description,
            detail: execution.detail,
            name: execution.extra
              ? `${config.name} ${execution.extra}`
              : config.name,
            status: execution.status,
            timeframe: execution.timeframe,
          }) satisfies NoMissing<Process.Step>,
      )
    })
  }
}
export namespace Template {
  // TODO: currently, this contains all of the templates properties, if that
  // doesn't change soon, then maybe we don't need a Template class an we can
  // just have an interface
  export interface Params<TId extends Template.Id = Template.Id>
    extends Pick<Template<TId>, 'id' | 'name' | 'description'> {
    /**
     * TODO:
     */
    steps: readonly StepConfig.Producer[]
  }

  /**
   * Identifies a {@link Template}
   */
  export enum Id {
    /**
     * Represents the importing of data (i.e. records) into the DMS from a file
     * upload
     */
    DmsDataImport = 'dms-data-import',
    /**
     * Represents the process of applying rules to RATE records and preparing
     * a proposal (pools, allocations, reports)
     */
    RatePrepareProposal = 'rate-prepare-proposal',
    /**
     * Represents the process of creating a scenario
     */
    CreateScenario = 'create-scenario',
    /**
     * Represents the generation of a space report file
     */
    SpaceReportGenerate = 'space-report-generate',
  }
  // Needs to be before namespace is defined
  const IDS: ReadonlySet<Id> = new Set(Object.values(Id) as Id[])
  export namespace Id {
    export const ALL: readonly Id[] = Array.from(IDS)
    export const isId = TypeChecker.Factory.oneOf(IDS)
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * TODO:
   */
  export interface StepConfig extends Difference<Process.Step, Execution> {
    /** TODO: */
    type: Activity.Type
    /**
     * Indicates that this {@link StepConfig} may not occur, and thus should not
     * appear in {@link Process.steps} until it becomes clear that it has
     * occurred or will occur
     *
     * @example
     * // TODO: true
     * // TODO: { until: [Activity.Type.OtherActivity1, Activity.Type.OtherActivity2] }
     */
    optional?:
      | true
      | {
          /**
           * Once every {@link Activity.Type} included here has occurred, then this
           * {@link StepConfig} is no longer {@link optional}
           */
          until: readonly Activity.Type[]
        }
    /**
     * TODO: used to match
     */
    item?: Item
  }
  export namespace StepConfig {
    /**
     * TODO:
     */
    export type Producer = ExactlyOneOf<{
      /**
       * TODO:
       */
      withoutItems: StepConfig
      /**
       * TODO:
       */
      basedOnItems: (item: readonly Readonly<Item>[]) => readonly StepConfig[]
    }>
  }
}
